import type { AdminCommerceCostSeriesPoint } from '../types/adminCommerce';

function sumCost(left: number, right: number): number {
  return Number((left + right).toFixed(12));
}

export function buildAdminCommerceCostTrendSeries(
  items: readonly AdminCommerceCostSeriesPoint[] | null | undefined,
): AdminCommerceCostTrendPoint[] {
  const byBucket = new Map<string, AdminCommerceCostTrendPoint>();
  for (const item of items ?? []) {
    const existing = byBucket.get(item.bucketAt);
    if (existing) {
      existing.totalCost = sumCost(existing.totalCost, item.totalCost);
      existing.inputCost = sumCost(existing.inputCost, item.inputCost);
      existing.outputCost = sumCost(existing.outputCost, item.outputCost);
      existing.cacheWriteCost = sumCost(
        existing.cacheWriteCost,
        item.cacheWriteCost,
      );
      existing.cacheReadCost = sumCost(
        existing.cacheReadCost,
        item.cacheReadCost,
      );
      existing.otherCost = sumCost(existing.otherCost, item.otherCost);
      existing.totalTokens += item.totalTokens;
      existing.requestCount += item.requestCount;
      continue;
    }
    byBucket.set(item.bucketAt, {
      bucketAt: item.bucketAt,
      groupKey: '',
      totalCost: item.totalCost,
      inputCost: item.inputCost,
      outputCost: item.outputCost,
      cacheWriteCost: item.cacheWriteCost,
      cacheReadCost: item.cacheReadCost,
      otherCost: item.otherCost,
      totalTokens: item.totalTokens,
      requestCount: item.requestCount,
    });
  }
  return [...byBucket.values()].sort((left, right) =>
    left.bucketAt.localeCompare(right.bucketAt),
  );
}

export type AdminCommerceCostTrendPoint = Pick<
  AdminCommerceCostSeriesPoint,
  | 'bucketAt'
  | 'groupKey'
  | 'totalCost'
  | 'inputCost'
  | 'outputCost'
  | 'cacheWriteCost'
  | 'cacheReadCost'
  | 'otherCost'
  | 'totalTokens'
  | 'requestCount'
>;

export interface AdminCommerceCostTrendLine {
  key: string;
  tokenKey: string;
  cacheRateKey: string;
  label: string;
  color: string;
}

export interface AdminCommerceCostTrendChart {
  data: Array<Record<string, string | number>>;
  lines: AdminCommerceCostTrendLine[];
}

const trendColors = [
  '#ea580c',
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#0891b2',
  '#7c3aed',
  '#ca8a04',
  '#475569',
];

const maxVisibleTrendLines = 12;

function trendLabel(groupKey: string): string {
  const label = groupKey || '全部';
  return label.replace(/^unknown \/ /, '历史未归因 / ');
}

export function buildAdminCommerceCostMultiSeries(
  items: readonly AdminCommerceCostSeriesPoint[] | null | undefined,
): AdminCommerceCostTrendChart {
  const totals = new Map<string, number>();
  for (const item of items ?? []) {
    const label = trendLabel(item.groupKey);
    totals.set(label, sumCost(totals.get(label) ?? 0, item.totalCost));
  }
  const rankedLabels = [...totals.entries()]
    .sort(
      ([leftLabel, leftTotal], [rightLabel, rightTotal]) =>
        rightTotal - leftTotal || leftLabel.localeCompare(rightLabel),
    )
    .map(([label]) => label);
  const labels = rankedLabels.slice(0, maxVisibleTrendLines);
  if (rankedLabels.length > maxVisibleTrendLines) labels.push('其他');
  const visibleLabels = new Set(labels);
  const lines = labels.map((label, index) => ({
    key: `cost_${index}`,
    tokenKey: `tokens_${index}`,
    cacheRateKey: `cache_rate_${index}`,
    label,
    color: trendColors[index % trendColors.length],
  }));
  const lineByLabel = new Map(lines.map((line) => [line.label, line]));
  const byBucket = new Map<string, Record<string, string | number>>();
  for (const item of items ?? []) {
    const row = byBucket.get(item.bucketAt) ?? { bucketAt: item.bucketAt };
    const rawLabel = trendLabel(item.groupKey);
    const label = visibleLabels.has(rawLabel) ? rawLabel : '其他';
    const line = lineByLabel.get(label);
    if (line) {
      row[line.key] = sumCost(Number(row[line.key] ?? 0), item.totalCost);
      row[line.tokenKey] = Number(row[line.tokenKey] ?? 0) + item.totalTokens;
      row[`${line.cacheRateKey}_read`] =
        Number(row[`${line.cacheRateKey}_read`] ?? 0) +
        Number(item.cacheReadTokens ?? 0);
      row[`${line.cacheRateKey}_eligible`] =
        Number(row[`${line.cacheRateKey}_eligible`] ?? 0) +
        Number(
          item.cacheEligibleTokens ??
            Math.max(
              Number(item.inputTokens ?? 0),
              Number(item.cacheReadTokens ?? 0) +
                Number(item.cacheWriteTokens ?? 0),
            ),
        );
    }
    byBucket.set(item.bucketAt, row);
  }
  const data = [...byBucket.values()].sort((left, right) =>
    String(left.bucketAt).localeCompare(String(right.bucketAt)),
  );
  for (const row of data) {
    for (const line of lines) {
      row[line.key] ??= 0;
      row[line.tokenKey] ??= 0;
      const readKey = `${line.cacheRateKey}_read`;
      const eligibleKey = `${line.cacheRateKey}_eligible`;
      const cacheRead = Number(row[readKey] ?? 0);
      const denominator = Number(row[eligibleKey] ?? 0);
      row[line.cacheRateKey] = denominator > 0 ? cacheRead / denominator : 0;
      delete row[readKey];
      delete row[eligibleKey];
    }
  }
  return { data, lines };
}
